import React from "react";
import { ColumnDef } from "@tanstack/react-table";

import DataTableColumnHeader from "../../../components/data-table/DataTableColumnHeader";
import StatusBadge from "../../../components/StatusBadge";
import { formatRelativeDateTime, truncateMiddle } from "../../../utils/formatters";
import { depositNairaValue } from "../../../utils/money";
import { formatAmount } from "../../../utils/wordFunctions";

export type DepositRow = {
  txid?: string;
  toAddress?: string;
  currency?: string;
  network?: string;
  amount?: number;
  usdtAmount?: number;
  processedAt?: string;
  createdAt?: string;
  rates?: { abbr: string; rate: number }[];
};

export const depositColumns: ColumnDef<DepositRow, any>[] = [
  {
    id: "processedAt",
    accessorFn: (row) => row.processedAt ?? row.createdAt,
    meta: { label: "Date" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }) => (
      <span className="text-[13px] font-medium text-foreground">
        {formatRelativeDateTime(row.original.processedAt ?? row.original.createdAt)}
      </span>
    ),
  },
  {
    accessorKey: "txid",
    meta: { label: "Transaction ID" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Transaction ID" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-[13px] text-muted-foreground">
        {truncateMiddle(row.original.txid, 8, 6)}
      </span>
    ),
  },
  {
    accessorKey: "toAddress",
    meta: { label: "To address" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="To address" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-[13px] text-muted-foreground">
        {truncateMiddle(row.original.toAddress, 8, 6)}
      </span>
    ),
  },
  {
    accessorKey: "amount",
    meta: { label: "Amount" },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => (
      <div>
        <p className="text-[14px] font-semibold text-foreground">
          {formatAmount(row.original.amount)}{" "}
          <span className="uppercase">{row.original.currency}</span>
        </p>
        <p className="text-[12px] font-medium text-muted-foreground">
          ${formatAmount(row.original.usdtAmount)}
        </p>
      </div>
    ),
  },
  {
    id: "nairaValue",
    accessorFn: (row) => depositNairaValue(row),
    meta: { label: "Naira value" },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Naira value" />
    ),
    cell: ({ row }) => (
      <span className="text-[14px] font-semibold text-foreground">
        {formatAmount(depositNairaValue(row.original))} NGN
      </span>
    ),
  },
  {
    id: "network",
    accessorKey: "network",
    meta: { label: "Network" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Network" />
    ),
    cell: ({ row }) => (
      <span className="text-[13px] font-medium uppercase text-muted-foreground">
        {row.original.network || "—"}
      </span>
    ),
  },
  {
    id: "status",
    meta: { label: "Status" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    // Deposits only reach this endpoint once they have settled.
    cell: () => <StatusBadge status="COMPLETED" />,
  },
];
